import * as React from "react";
import { RefreshCw } from "Components/ui/icons";

import { getApiErrorMessage } from "api/apiError";
import { Alert, Button, Card, ErrorState, Page, PageHeader } from "Components/ui";
import { useDashboardData } from "./data";
import { formatTimeOfDay } from "./format";
import BillingCard from "./Components/BillingCard";
import FleetVerdict from "./Components/FleetVerdict";
import IncidentsCard from "./Components/IncidentsCard";
import ServerCard from "./Components/ServerCard";
import TenantBoard from "./Components/TenantBoard";

// "Wednesday, 28 October 2026" in IST (assembled from parts; locales disagree on commas).
const todayLabel = (() => {
  const parts = new Intl.DateTimeFormat("en-GB", {
    timeZone: "Asia/Kolkata",
    weekday: "long",
    day: "numeric",
    month: "long",
    year: "numeric",
  }).formatToParts(new Date());
  const part = (type: string) => parts.find((entry) => entry.type === type)?.value || "";
  return `${part("weekday")}, ${part("day")} ${part("month")} ${part("year")}`;
})();

/**
 * Fleet health, incidents and billing at a glance; every number comes from a live API.
 * Bounded widgets come first and the tenant board, which grows with the fleet, comes last. On wide
 * screens incidents and billing sit in a rail that stays in view while the board scrolls.
 */
const Dashboard = () => {
  const {
    canSeeFleet,
    canSeeBilling,
    overview,
    history,
    billing,
    rows,
    groups,
    lastCheckedAt,
    refresh,
    refreshing,
  } = useDashboardData();
  const fleet = overview.data;

  return (
    <Page width="wide">
      <PageHeader
        title="Dashboard"
        description={
          fleet ? `${todayLabel} · updated ${formatTimeOfDay(fleet.generatedAt)} IST` : todayLabel
        }
        actions={
          <Button
            variant="secondary"
            size="sm"
            leftIcon={<RefreshCw />}
            loading={refreshing}
            onClick={refresh}
          >
            Refresh
          </Button>
        }
      />

      {!canSeeFleet ? (
        <>
          <Alert tone="neutral" title="Tenant health is hidden">
            Your role can't read tenants. Ask a super admin for tenant read access to see the fleet
            here.
          </Alert>
          {canSeeBilling && (
            <div className="max-w-md">
              <BillingCard billing={billing} />
            </div>
          )}
        </>
      ) : overview.isError ? (
        <Card>
          <ErrorState
            title="The fleet didn't load"
            description={getApiErrorMessage(overview.error)}
            action={
              <Button variant="secondary" onClick={() => overview.refetch()}>
                Try again
              </Button>
            }
          />
        </Card>
      ) : (
        <div className="grid gap-6 xl:grid-cols-[minmax(0,1fr)_22rem]">
          <FleetVerdict
            className="xl:col-span-2"
            summary={fleet?.summary}
            lastCheckedAt={lastCheckedAt}
            loading={overview.isLoading}
          />
          <ServerCard
            className="self-start xl:col-start-1 xl:row-start-2"
            server={fleet?.server}
            self={fleet?.self}
            thresholds={fleet?.thresholds}
            history={history}
            loading={overview.isLoading}
          />
          <aside
            aria-label="Incidents and billing"
            className="xl:col-start-2 xl:row-span-2 xl:row-start-2"
          >
            <div className="space-y-6 xl:sticky xl:top-20">
              {canSeeBilling && <BillingCard billing={billing} />}
              <IncidentsCard history={history} tenants={rows} />
            </div>
          </aside>
          <TenantBoard
            className="self-start xl:col-start-1 xl:row-start-3"
            groups={groups}
            untracked={fleet?.untracked || []}
            host={fleet?.server.host || null}
            loading={overview.isLoading}
          />
        </div>
      )}
    </Page>
  );
};

export default Dashboard;
